// client/src/components/ProductCard.js
import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Box, Card, CardActionArea, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { formatCurrency, getSavingsPercent } from '../utils/currency';
import { API_BASE_URL, getProductImageUrl } from '../utils/media';
import { LOW_STOCK_THRESHOLD } from '../constants/inventory';
import { isInCompare, toggleCompare } from '../utils/compareList';
import { useGeo } from '../context/GeoContext';

const cornerButtonSx = {
  bgcolor: 'background.paper', border: 1, borderColor: 'divider',
  '&:hover': { bgcolor: 'background.paper', borderColor: 'text.secondary' },
};

const ProductCard = ({ product, width, sx }) => {
  const { addToCart } = useContext(CartContext);
  const { token } = useContext(AuthContext);
  const { isEnabled } = useContext(FeatureFlagContext);
  const { formatLocalPrice } = useGeo();
  const [wishlisted, setWishlisted] = useState(false);
  const [inCompare, setInCompare] = useState(() => isInCompare(product.id));

  const quantity = Number(product.quantity);
  const inStock = quantity > 0;
  const lowStock = inStock && quantity <= LOW_STOCK_THRESHOLD;
  const hasVariants = Boolean(product.has_variants);
  const savingsPercent = getSavingsPercent(product.price, product.compare_at_price);

  const handleCompareToggle = () => {
    const { added, limitReached } = toggleCompare(product.id);
    if (limitReached) {
      alert('You can compare up to 4 products at a time. Remove one first.');
      return;
    }
    setInCompare(added);
  };

  const handleWishlist = async () => {
    if (!token) {
      alert('Please log in to add items to your wishlist.');
      return;
    }
    try {
      await fetch(`${API_BASE_URL}/api/wishlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ productId: product.id }),
      });
      setWishlisted(true);
      setTimeout(() => setWishlisted(false), 2000);
    } catch (err) {
      alert('Failed to add to wishlist.');
    }
  };

  return (
    <Card
      sx={{
        width: width || '100%', display: 'flex', flexDirection: 'column', flexShrink: 0, position: 'relative',
        '&:hover': { borderColor: 'text.secondary' },
        ...sx,
      }}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5, zIndex: 1 }}>
        <Tooltip title={inCompare ? 'Remove from compare' : 'Add to compare'}>
          <IconButton size="small" onClick={handleCompareToggle} aria-label="Toggle compare" color={inCompare ? 'primary' : 'default'} sx={cornerButtonSx}>
            <CompareArrowsIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {isEnabled('wishlist') && (
          <Tooltip title="Add to wishlist">
            <IconButton size="small" onClick={handleWishlist} aria-label="Add to wishlist" sx={cornerButtonSx}>
              {wishlisted ? <FavoriteIcon color="error" sx={{ fontSize: 18 }} /> : <FavoriteBorderIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <CardActionArea component={Link} to={`/products/${product.id}`} sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <Box sx={{ position: 'relative', overflow: 'hidden', aspectRatio: '1 / 1', bgcolor: 'action.hover' }}>
          <Box
            component="img"
            src={getProductImageUrl(product.imageUrl)}
            alt={product.name}
            loading="lazy"
            sx={{
              width: '100%', height: '100%', objectFit: 'contain', p: 2.5, display: 'block',
              transition: 'transform 0.4s ease',
              '.MuiCard-root:hover &': { transform: 'scale(1.04)' },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none', '.MuiCard-root:hover &': { transform: 'none' } },
            }}
          />
          {savingsPercent && (
            <Chip label={`-${savingsPercent}%`} color="error" size="small" sx={{ position: 'absolute', top: 10, left: 10 }} />
          )}
          {(product.condition_grade || product.build_type) && (
            <Chip
              label={product.condition_grade ? `Pre-owned: ${product.condition_grade}` : product.build_type}
              size="small"
              sx={{ position: 'absolute', bottom: 8, left: 8, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}
            />
          )}
        </Box>
        <Box sx={{ p: 2, pb: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {product.category && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontWeight: 600, mb: 0.25 }}>{product.category}</Typography>
          )}
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.7em', mb: 1 }}
          >
            {product.name}
          </Typography>
          {product.store_name && (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ mb: 0.5 }}>Sold by {product.store_name}</Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, mt: 'auto' }}>
            <Typography variant="h6" component="div" fontWeight={800}>{formatCurrency(product.price)}</Typography>
            {savingsPercent && (
              <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                {formatCurrency(product.compare_at_price)}
              </Typography>
            )}
          </Box>
          {formatLocalPrice(product.price) && (
            <Typography variant="caption" color="text.secondary">≈ {formatLocalPrice(product.price)}</Typography>
          )}
          {lowStock && (
            <Typography variant="caption" color="error.main" fontWeight={600}>Only {quantity} left in stock</Typography>
          )}
        </Box>
      </CardActionArea>

      <Box sx={{ px: 2, pb: 2 }}>
        {!inStock ? (
          <Button fullWidth variant="outlined" disabled>Out of stock</Button>
        ) : hasVariants ? (
          <Button fullWidth variant="outlined" component={Link} to={`/products/${product.id}`}>Select options</Button>
        ) : (
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddShoppingCartIcon fontSize="small" />}
            onClick={() => addToCart(product)}
            sx={{ whiteSpace: 'nowrap', px: { xs: 1, sm: 2.5 }, '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'inherit' } } }}
          >
            Add to cart
          </Button>
        )}
      </Box>
    </Card>
  );
};

export default ProductCard;
